import React from 'react';
import type { ScsReviewQuarter } from '../../service/types';

export default function ScsReviewTabs({
  quarters,
  selected,
  current,
  dirty,
  onSelect,
}: {
  quarters: Record<string, ScsReviewQuarter>;
  selected: string;
  current: string;
  dirty: Record<string, boolean>;
  onSelect: (quarter: string) => void;
}) {
  const entries = Object.values(quarters).sort((a, b) => a.quarter.localeCompare(b.quarter));
  const enabled = entries.filter((entry) => entry.available);
  return (
    <div
      role="tablist"
      aria-label="Manager review quarter"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}
    >
      {entries.map((entry) => {
        const active = selected === entry.quarter;
        const earlierIncomplete = entry.quarter < current && !entry.managerComplete;
        return (
          <button
            key={entry.quarter}
            type="button"
            role="tab"
            id={`review-tab-${entry.quarter}`}
            aria-controls={`review-panel-${entry.quarter}`}
            aria-selected={active}
            disabled={!entry.available}
            tabIndex={active ? 0 : -1}
            onClick={() => onSelect(entry.quarter)}
            onKeyDown={(event) => {
              const index = enabled.findIndex((item) => item.quarter === entry.quarter);
              const next =
                event.key === 'ArrowRight'
                  ? enabled[(index + 1) % enabled.length]
                  : event.key === 'ArrowLeft'
                    ? enabled[(index + enabled.length - 1) % enabled.length]
                    : event.key === 'Home'
                      ? enabled[0]
                      : event.key === 'End'
                        ? enabled[enabled.length - 1]
                        : null;
              if (!next) return;
              event.preventDefault();
              onSelect(next.quarter);
              document.getElementById(`review-tab-${next.quarter}`)?.focus();
            }}
            className="govuk-body"
            style={{
              flex: '1 1 150px',
              minHeight: 76,
              padding: 12,
              margin: 0,
              border: active ? '3px solid #00703c' : '1px solid #b1b4b6',
              background: active
                ? '#eef7f0'
                : earlierIncomplete || !entry.available
                  ? '#f3f2f1'
                  : 'white',
              color: !entry.available || earlierIncomplete ? '#505a5f' : '#0b0c0c',
              textAlign: 'left',
              cursor: entry.available ? 'pointer' : 'default',
            }}
          >
            <strong>
              {entry.quarter}
              {!entry.available && (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                  focusable="false"
                  style={{ marginLeft: 8 }}
                >
                  <rect x="3" y="7" width="10" height="7" rx="1" />
                  <path d="M5 7V5a3 3 0 016 0v2" />
                </svg>
              )}
              {dirty[entry.quarter] ? ' - unsaved' : ''}
            </strong>
            <br />
            <span className="govuk-body-s">
              {!entry.available
                ? `Unlocks ${new Date(`${entry.opensOn}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`
                : entry.managerComplete
                  ? 'Complete'
                  : earlierIncomplete
                    ? 'Earlier - incomplete'
                    : 'Current quarter'}
            </span>
          </button>
        );
      })}
    </div>
  );
}
